import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CATEGORIES,
  acceptAll,
  getConsent,
  handleGpcSignal,
  hasDecided,
  onConsentChange,
  openPreferences as openPrefsExt,
  OPEN_PREFS_EVENT,
  POLICY_URL,
  rejectAll,
  setConsent,
  type Category,
} from "../lib/consent";

type Mode = "hidden" | "card" | "prefs";

export default function ConsentBanner() {
  const [mode, setMode] = useState<Mode>("hidden");

  // Local copy of the per-category toggles (used by the prefs modal).
  // Defaults: strictly_necessary on, others off (matches strict opt-in).
  const [draft, setDraft] = useState<Record<Category, boolean>>({
    strictly_necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Boot: honour any GPC signal, then decide if we show the card.
    handleGpcSignal();
    const decided = hasDecided();
    if (!decided) setMode("card");

    // Seed draft from existing record (if any)
    const existing = getConsent();
    if (existing) setDraft(existing.categories);

    // Other parts of the app can trigger the modal via a custom event
    const openHandler = () => {
      const current = getConsent();
      if (current) setDraft(current.categories);
      setMode("prefs");
    };
    window.addEventListener(OPEN_PREFS_EVENT, openHandler);
    const unsubscribe = onConsentChange((rec) => setDraft(rec.categories));

    return () => {
      window.removeEventListener(OPEN_PREFS_EVENT, openHandler);
      unsubscribe();
    };
  }, []);

  if (mode === "hidden") return null;

  const closeAfterDecision = () => setMode("hidden");

  const onAcceptAll = () => {
    acceptAll();
    closeAfterDecision();
  };
  const onRejectAll = () => {
    rejectAll();
    closeAfterDecision();
  };
  const onSaveChoices = () => {
    setConsent(
      { analytics: draft.analytics, marketing: draft.marketing },
      { method: "save_choices" },
    );
    closeAfterDecision();
  };

  return (
    <>
      {/* ============ FLOATING CARD (first visit) ============ */}
      {mode === "card" && (
        <div
          role="dialog"
          aria-labelledby="consent-card-title"
          aria-describedby="consent-card-body"
          className="fixed z-[60] left-3 right-3 sm:left-4 sm:right-auto bottom-3 sm:bottom-4 sm:max-w-[26rem] animate-fadeUp"
          style={{
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <div
            className="rounded-2xl border border-white p-5 sm:p-6"
            style={{
              background: "rgba(255,255,255,0.98)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              boxShadow:
                "0 28px 60px -24px rgba(91,63,228,0.32), 0 14px 36px -18px rgba(11,13,35,0.18)",
            }}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #26a8a2 0%, #5b3fe4 100%)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z" />
                  <circle cx="8.5" cy="11.5" r="0.6" />
                  <circle cx="13" cy="15" r="0.6" />
                  <circle cx="15.5" cy="9.5" r="0.6" />
                </svg>
              </span>
              <div className="min-w-0">
                <h2
                  id="consent-card-title"
                  className="font-display text-lg leading-tight text-ink-900"
                >
                  Cookies are off by default.
                </h2>
                <p
                  id="consent-card-body"
                  className="mt-1.5 text-sm leading-relaxed text-ink-500"
                >
                  Only strictly-necessary cookies run unless you say
                  otherwise. We don't pre-tick anything.{" "}
                  <Link
                    to="/cookies"
                    className="text-royal hover:text-royal-hi underline underline-offset-2"
                  >
                    Cookie policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onAcceptAll}
                className="btn-primary text-sm py-2 px-4"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={onRejectAll}
                className="btn-ghost text-sm py-2 px-4"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => setMode("prefs")}
                className="ml-auto text-xs font-medium text-royal hover:text-royal-hi underline-offset-2 hover:underline"
              >
                Customize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ PREFERENCES MODAL ============ */}
      {mode === "prefs" && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="consent-prefs-title"
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-3 sm:px-6 py-4 sm:py-8"
        >
          {/* backdrop */}
          <button
            type="button"
            aria-label="Close cookie preferences"
            onClick={() => setMode(hasDecided() ? "hidden" : "card")}
            className="absolute inset-0 bg-ink-900/30 backdrop-blur-[2px]"
          />
          <div
            className="relative w-full sm:max-w-2xl max-h-[88dvh] overflow-y-auto rounded-2xl border border-white animate-fadeUp"
            style={{
              background: "rgba(255,255,255,0.99)",
              boxShadow:
                "0 40px 100px -32px rgba(91,63,228,0.35), 0 14px 36px -18px rgba(11,13,35,0.22)",
            }}
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="pill pill-lav">Cookie preferences</span>
                  <h2
                    id="consent-prefs-title"
                    className="mt-3 font-display text-3xl leading-tight"
                  >
                    Choose what runs on your visit.
                  </h2>
                  <p className="mt-3 text-ink-500 leading-relaxed text-sm">
                    Default-deny: nothing non-essential runs until you opt in.
                    You can change this any time from the footer.{" "}
                    <Link
                      to="/cookies"
                      className="text-royal hover:text-royal-hi underline underline-offset-2"
                    >
                      Read the full policy
                    </Link>
                    .
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMode(hasDecided() ? "hidden" : "card")}
                  aria-label="Close"
                  className="shrink-0 rounded-full p-2 text-ink-500 hover:bg-cream-100 hover:text-ink-800"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </div>

              <div className="mt-6 space-y-3">
                {CATEGORIES.map((cat) => {
                  const isOn = !!draft[cat.id];
                  const locked = cat.required;
                  return (
                    <div
                      key={cat.id}
                      className={`rounded-xl border p-4 sm:p-5 transition ${
                        isOn
                          ? "border-royal/40 bg-lav-50"
                          : "border-ink-200/50 bg-white/70"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-ink-900">
                              {cat.label}
                            </span>
                            {locked && (
                              <span className="font-mono text-[10px] uppercase tracking-widest rounded-full bg-ink-100 px-2 py-0.5 text-ink-500 border border-ink-200/50">
                                Always on
                              </span>
                            )}
                          </div>
                          <p className="mt-1.5 text-sm text-ink-500 leading-relaxed">
                            {cat.description}
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isOn}
                          aria-label={`Toggle ${cat.label}`}
                          disabled={locked}
                          onClick={() =>
                            !locked &&
                            setDraft((d) => ({ ...d, [cat.id]: !d[cat.id] }))
                          }
                          className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition ${
                            isOn
                              ? "bg-royal"
                              : "bg-ink-200"
                          } ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              isOn ? "translate-x-5" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={onSaveChoices}
                  className="btn-primary"
                >
                  Save my choices
                </button>
                <button
                  type="button"
                  onClick={onAcceptAll}
                  className="btn-ghost"
                >
                  Accept all
                </button>
                <button
                  type="button"
                  onClick={onRejectAll}
                  className="btn-ghost"
                >
                  Reject non-essential
                </button>
                <span className="ml-auto text-xs text-ink-400 font-mono uppercase tracking-[0.15em]">
                  v{CATEGORIES.length > 0 ? "1.0" : "1.0"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Re-export so other code can do: import { openPreferences } from
// "@/components/ConsentBanner" and get a callable handle.
export const openPreferences = openPrefsExt;
export const PolicyUrl = POLICY_URL;
